import { cn } from '@/lib/utils';

/**
 * The frame every assistant card shares. Spans, not divs: a card lives
 * inside a markdown paragraph, so its markup has to be valid there.
 */

/** Card frame, styled as a block card. */
export function CardShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="my-2 block w-full overflow-hidden rounded-xl border border-admin-line bg-white text-portal-small shadow-[0_1px_2px_rgba(17,40,83,.05),0_4px_14px_rgba(17,40,83,.05)]">
      {children}
    </span>
  );
}

/** The padded content area inside a card. */
export function CardBody({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="block p-3.5">{children}</span>;
}

/**
 * The leading icon badge every card shares — brand-tinted by default, muted for
 * de-emphasised cards (e.g. a return authorization).
 */
export function CardBadge({ children, muted }: { children: React.ReactNode; muted?: boolean }): React.ReactElement {
  return (
    <span className={cn('grid size-9 shrink-0 place-items-center rounded-[10px]', muted ? 'bg-admin-subtle text-admin-muted' : 'bg-brand/10 text-brand')}>
      {children}
    </span>
  );
}

/** The small uppercase category label at the top of a card. */
export function CardEyebrow({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="block text-[10px] font-extrabold uppercase tracking-wide text-admin-muted">{children}</span>;
}

/** The primary action inside a card. */
export function CardPrimaryButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white hover:brightness-110 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/** The quiet action inside a card (Dismiss). */
export function CardQuietButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-admin-micro font-bold text-admin-muted hover:text-portal-ink"
    >
      {children}
    </button>
  );
}
